import { oc } from "@orpc/contract";
import { z } from "zod";

const UUIDSchema = z.uuid();

// ---------------------------------------------------------------------------
// Schemas
// ---------------------------------------------------------------------------

export const InternalUiTemplateVersionStatusSchema = z.enum([
  "draft",
  "finalized",
]);

export const InternalUiTemplateSchema = z.object({
  id: UUIDSchema,
  organizationId: UUIDSchema,
  name: z.string(),
  jsonSpec: z.record(z.string(), z.unknown()),
  sampleState: z.record(z.string(), z.unknown()).nullable(),
  isDefault: z.boolean(),
  isActive: z.boolean(),
  pinnedVersionId: UUIDSchema.nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export const InternalUiTemplateVersionSchema = z.object({
  id: UUIDSchema,
  internalUiTemplateId: UUIDSchema,
  parentVersionId: UUIDSchema.nullable(),
  status: InternalUiTemplateVersionStatusSchema,
  version: z.number().int(),
  changelog: z.string().nullable(),
  createdBy: UUIDSchema,
  jsonSpec: z.record(z.string(), z.unknown()),
  sampleState: z.record(z.string(), z.unknown()).nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

// ---------------------------------------------------------------------------
// List
// ---------------------------------------------------------------------------

export const ListInternalUiTemplatesInputSchema = z.object({});
export const ListInternalUiTemplatesResponseSchema = z.object({
  templates: z.array(InternalUiTemplateSchema),
});

// ---------------------------------------------------------------------------
// Get
// ---------------------------------------------------------------------------

export const GetInternalUiTemplateInputSchema = z.object({ id: UUIDSchema });
export const GetInternalUiTemplateResponseSchema = z.object({
  template: InternalUiTemplateSchema,
});

// ---------------------------------------------------------------------------
// Create
// ---------------------------------------------------------------------------

export const CreateInternalUiTemplateInputSchema = z.object({
  name: z.string().min(1),
  jsonSpec: z.record(z.string(), z.unknown()),
  sampleState: z.record(z.string(), z.unknown()).nullable().optional(),
  isDefault: z.boolean().optional(),
});
export const CreateInternalUiTemplateResponseSchema = z.object({
  template: InternalUiTemplateSchema,
});

// ---------------------------------------------------------------------------
// Update
// ---------------------------------------------------------------------------

export const UpdateInternalUiTemplateInputSchema = z.object({
  id: UUIDSchema,
  name: z.string().min(1).optional(),
  jsonSpec: z.record(z.string(), z.unknown()).optional(),
  sampleState: z.record(z.string(), z.unknown()).nullable().optional(),
  isActive: z.boolean().optional(),
});
export const UpdateInternalUiTemplateResponseSchema = z.object({
  template: InternalUiTemplateSchema,
});

// ---------------------------------------------------------------------------
// Delete
// ---------------------------------------------------------------------------

export const DeleteInternalUiTemplateInputSchema = z.object({ id: UUIDSchema });
export const DeleteInternalUiTemplateResponseSchema = z.object({
  success: z.boolean(),
});

// ---------------------------------------------------------------------------
// Validate Preview (internal UI renders client-side, so the server validates)
// ---------------------------------------------------------------------------

export const ValidateInternalUiPreviewInputSchema = z.object({
  jsonSpec: z.record(z.string(), z.unknown()),
});
export const ValidateInternalUiPreviewResponseSchema = z.object({
  valid: z.boolean(),
  issues: z.array(z.string()),
});

// ---------------------------------------------------------------------------
// List Versions
// ---------------------------------------------------------------------------

export const ListInternalUiTemplateVersionsInputSchema = z.object({
  internalUiTemplateId: UUIDSchema,
});
export const ListInternalUiTemplateVersionsResponseSchema = z.object({
  versions: z.array(InternalUiTemplateVersionSchema),
});

// ---------------------------------------------------------------------------
// Create Draft
// ---------------------------------------------------------------------------

export const CreateInternalUiTemplateDraftInputSchema = z.object({
  internalUiTemplateId: UUIDSchema,
  jsonSpec: z.record(z.string(), z.unknown()).optional(),
  sampleState: z.record(z.string(), z.unknown()).nullable().optional(),
});
export const CreateInternalUiTemplateDraftResponseSchema = z.object({
  version: InternalUiTemplateVersionSchema,
});

// ---------------------------------------------------------------------------
// Update Draft
// ---------------------------------------------------------------------------

export const UpdateInternalUiTemplateDraftInputSchema = z.object({
  versionId: UUIDSchema,
  jsonSpec: z.record(z.string(), z.unknown()).optional(),
  sampleState: z.record(z.string(), z.unknown()).nullable().optional(),
});
export const UpdateInternalUiTemplateDraftResponseSchema = z.object({
  version: InternalUiTemplateVersionSchema,
});

// ---------------------------------------------------------------------------
// Finalize Draft
// ---------------------------------------------------------------------------

export const FinalizeInternalUiTemplateDraftInputSchema = z.object({
  versionId: UUIDSchema,
});
export const FinalizeInternalUiTemplateDraftResponseSchema = z.object({
  version: InternalUiTemplateVersionSchema,
});

// ---------------------------------------------------------------------------
// Publish Version
// ---------------------------------------------------------------------------

export const PublishInternalUiTemplateVersionInputSchema = z.object({
  internalUiTemplateId: UUIDSchema,
  versionId: UUIDSchema,
});
export const PublishInternalUiTemplateVersionResponseSchema = z.object({
  template: InternalUiTemplateSchema,
});

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export const internalUiTemplatesContracts = {
  list: oc
    .input(ListInternalUiTemplatesInputSchema)
    .output(ListInternalUiTemplatesResponseSchema)
    .route({ method: "GET" }),
  get: oc
    .input(GetInternalUiTemplateInputSchema)
    .output(GetInternalUiTemplateResponseSchema)
    .route({ method: "GET" }),
  create: oc
    .input(CreateInternalUiTemplateInputSchema)
    .output(CreateInternalUiTemplateResponseSchema)
    .route({ method: "POST" }),
  update: oc
    .input(UpdateInternalUiTemplateInputSchema)
    .output(UpdateInternalUiTemplateResponseSchema)
    .route({ method: "POST" }),
  delete: oc
    .input(DeleteInternalUiTemplateInputSchema)
    .output(DeleteInternalUiTemplateResponseSchema)
    .route({ method: "POST" }),
  validatePreview: oc
    .input(ValidateInternalUiPreviewInputSchema)
    .output(ValidateInternalUiPreviewResponseSchema)
    .route({ method: "POST" }),
  listVersions: oc
    .input(ListInternalUiTemplateVersionsInputSchema)
    .output(ListInternalUiTemplateVersionsResponseSchema)
    .route({ method: "GET" }),
  createDraft: oc
    .input(CreateInternalUiTemplateDraftInputSchema)
    .output(CreateInternalUiTemplateDraftResponseSchema)
    .route({ method: "POST" }),
  updateDraft: oc
    .input(UpdateInternalUiTemplateDraftInputSchema)
    .output(UpdateInternalUiTemplateDraftResponseSchema)
    .route({ method: "POST" }),
  finalizeDraft: oc
    .input(FinalizeInternalUiTemplateDraftInputSchema)
    .output(FinalizeInternalUiTemplateDraftResponseSchema)
    .route({ method: "POST" }),
  publishVersion: oc
    .input(PublishInternalUiTemplateVersionInputSchema)
    .output(PublishInternalUiTemplateVersionResponseSchema)
    .route({ method: "POST" }),
} as const;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type InternalUiTemplateResponse = z.infer<
  typeof InternalUiTemplateSchema
>;
export type InternalUiTemplateVersionResponse = z.infer<
  typeof InternalUiTemplateVersionSchema
>;
